import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useDocumentTitle, useMe } from '../lib/hooks';
import { CLASSE_COR, classificar, fmtDayMon } from '../lib/format';
import { NcSheet } from '../components/NcSheet';
import type { OpenNc, Permission, Reply, VisitSummary } from '../lib/types';
import { Avatar, Button, ErrorState, Loading, Sheet, Stat, VisitRow } from '../components/ui';
import { Comments, ReportHtml, useReportCss } from '../components/ReportBody';
import { PdfButton } from '../components/PdfButton';
import { AccountSheet } from '../components/AppShell';
import { useToast } from '../components/feedback';

interface PortalHome {
  user: { nome: string };
  restaurants: Array<{
    id: string; nome: string; tenantNome: string; nutriNome: string | null; permission: Permission; permLabel: string; canReply: boolean;
    stats: { abertas: number; ultimaVisita: number | null; corrigidas: number; notaMedia: number | null };
    pending: OpenNc[];
    visits: VisitSummary[];
  }>;
  reports: VisitSummary[];
  periods: Array<{ tenantId: string; kind: string; inicio: string; label: string; url: string }>;
}

function PortalHeader({ title }: { title: string }) {
  const me = useMe().data;
  const [account, setAccount] = useState(false);
  return (
    <header className="topbar">
      <div className="topbar-in" style={{ maxWidth: 820 }}>
        <Avatar name={title} size={30} round={false} color="#5F7386" />
        <div className="flex1">
          <div className="tname ellipsis">{title}</div>
          <div className="trole">Portal do cliente</div>
        </div>
        {(me?.tenants.length ?? 0) > 0 && <Link to="/plataformas" className="btn xs none" style={{ color: 'var(--text-3)' }}>Plataformas</Link>}
        <button className="none" style={{ border: 0, background: 'none', padding: 0 }} onClick={() => setAccount(true)} aria-label="Sua conta">
          <Avatar name={me?.user.nome ?? '·'} size={34} color="#22282A" />
        </button>
      </div>
      {account && <AccountSheet open onClose={() => setAccount(false)} />}
    </header>
  );
}

function ReplySheet({ nc, onClose, onSaved }: { nc: { ncId: string; titulo: string; setor: string; reply: Reply | null } | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [lastNc, setLastNc] = useState<string | null>(null);
  if (nc && nc.ncId !== lastNc) {
    setLastNc(nc.ncId);
    setText(nc.reply?.texto ?? '');
  }
  const save = useMutation({
    mutationFn: () => api.post(`/api/portal/ncs/${nc!.ncId}/replies`, { texto: text.trim() }),
    onSuccess: () => { toast('Plano de ação enviado'); onSaved(); onClose(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  return (
    <Sheet open={!!nc} onClose={onClose} title="Plano de ação" subtitle={nc ? `${nc.titulo} · ${nc.setor}` : undefined}>
      <textarea className="textarea" rows={4} placeholder="O que será feito e até quando" value={text} onChange={(e) => setText(e.target.value)} style={{ marginBottom: 14 }} />
      <Button variant="dark" size="lg" block loading={save.isPending} disabled={text.trim().length < 3} onClick={() => save.mutate()}>Enviar à nutricionista</Button>
    </Sheet>
  );
}

export function Portal() {
  useDocumentTitle('Portal do cliente');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['portal'], queryFn: () => api.get<PortalHome>('/api/portal/home') });
  const [replying, setReplying] = useState<OpenNc | null>(null);
  const [ncOpen, setNcOpen] = useState<string | null>(null);
  const d = q.data;
  const title = d?.restaurants.length === 1 && !d.reports.length ? d.restaurants[0].nome : 'Compartilhados comigo';
  const tenants = [...new Set(d?.restaurants.map((r) => r.tenantNome) ?? [])];

  return (
    <div style={{ minHeight: '100vh' }}>
      <PortalHeader title={d ? title : ' '} />
      <main className="page anim-fade" style={{ maxWidth: 820, paddingBottom: 60 }}>
        {q.error && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
        {!d && !q.error && <Loading rows={4} height={90} />}
        {d && (
          <>
            <h1 className="h1" style={{ marginBottom: 3 }}>Olá, {d.user.nome.split(' ')[0]}</h1>
            <div className="sub" style={{ marginBottom: 18 }}>
              {tenants.length === 1 ? `Acompanhe as visitas técnicas da ${tenants[0]} e responda às pendências.` : 'Acompanhe as visitas técnicas e responda às pendências.'}
            </div>

            {d.restaurants.map((r) => (
              <section key={r.id} style={{ marginBottom: 28 }}>
                {d.restaurants.length > 1 && <h2 className="h2" style={{ fontSize: 17, marginBottom: 10 }}>{r.nome}</h2>}
                <div className="grid-stats" style={{ marginBottom: 22 }}>
                  {r.stats.notaMedia != null && (
                    <Stat small value={r.stats.notaMedia} label={`Nota média · ${classificar(r.stats.notaMedia)}`} color={CLASSE_COR[classificar(r.stats.notaMedia)].fg} />
                  )}
                  <Stat small value={r.stats.abertas} label="Pendências abertas" color="var(--nc)" />
                  <Stat small value={r.stats.ultimaVisita ? fmtDayMon(r.stats.ultimaVisita) : '—'} label="Última visita" />
                  <Stat small value={r.stats.corrigidas} label="Corrigidas no ciclo" color="var(--resolved)" />
                </div>
                {r.nutriNome && <div className="small" style={{ margin: '-12px 0 18px' }}>Nutricionista responsável: <strong style={{ color: 'var(--text)', fontWeight: 500 }}>{r.nutriNome}</strong> · seu acesso: {r.permLabel}</div>}

                <h2 className="h2" style={{ marginBottom: 10 }}>Pendências para corrigir</h2>
                <div className="stack gap-8" style={{ marginBottom: 24 }}>
                  {r.pending.map((n) => (
                    <div key={n.ncId} className="card" style={{ padding: 14, borderRadius: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.35 }}>{n.titulo}</div>
                      <div className="small" style={{ marginTop: 3 }}>{n.setor} · apontada em {fmtDayMon(n.openedAt)}{n.ageVisits > 1 ? ` · persiste há ${n.ageVisits} visitas` : ''}</div>
                      {n.reply && <div className="reply" style={{ marginTop: 9, fontSize: 12.5 }}><span style={{ fontWeight: 500 }}>Seu plano de ação:</span> {n.reply.texto}</div>}
                      <div className="row wrap gap-8" style={{ marginTop: 11 }}>
                        <Button size="sm" onClick={() => setNcOpen(n.ncId)}>Ver fotos</Button>
                        {r.canReply && (
                          <Button size="sm" variant="dark" onClick={() => setReplying(n)}>{n.reply ? 'Editar plano de ação' : 'Responder com plano de ação'}</Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {r.pending.length === 0 && <div className="empty">Nenhuma pendência aberta. 👏</div>}
                </div>

                <h2 className="h2" style={{ marginBottom: 10 }}>Relatórios compartilhados com você</h2>
                <div className="stack gap-8">
                  {r.visits.map((v) => <VisitRow key={v.id} v={v} to={`/portal/relatorio/${v.id}`} showRest={false} title={`Visita nº ${v.numero}`} cta="Abrir →" />)}
                  {r.visits.length === 0 && <div className="empty">Nenhuma visita registrada ainda.</div>}
                </div>
              </section>
            ))}

            {d.reports.length > 0 && (
              <section style={{ marginBottom: 28 }}>
                <h2 className="h2" style={{ marginBottom: 10 }}>Relatórios de visita</h2>
                <div className="stack gap-8">
                  {d.reports.map((v) => <VisitRow key={v.id} v={v} to={`/portal/relatorio/${v.id}`} cta="Abrir →" />)}
                </div>
              </section>
            )}
            {d.periods.length > 0 && (
              <section>
                <h2 className="h2" style={{ marginBottom: 10 }}>Relatórios de período</h2>
                <div className="stack gap-8">
                  {d.periods.map((p) => (
                    <a key={p.url} href={p.url} className="item"><div className="flex1" style={{ fontSize: 13.5, fontWeight: 500 }}>{p.label}</div><span style={{ color: 'var(--accent)', fontSize: 12.5 }}>Abrir →</span></a>
                  ))}
                </div>
              </section>
            )}
            {d.restaurants.length === 0 && d.reports.length === 0 && d.periods.length === 0 && (
              <div className="empty">Nada foi compartilhado com você ainda.</div>
            )}
          </>
        )}
      </main>
      <ReplySheet nc={replying} onClose={() => setReplying(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['portal'] })} />
      <NcSheet base="/api/portal/ncs" ncId={ncOpen} onClose={() => setNcOpen(null)} reportHref={(vid) => `/portal/relatorio/${vid}`} />
    </div>
  );
}

export function PortalReport() {
  const { vid } = useParams() as { vid: string };
  const nav = useNavigate();
  const qc = useQueryClient();
  const css = useReportCss();
  const [replying, setReplying] = useState<{ ncId: string; titulo: string; setor: string; reply: Reply | null } | null>(null);
  const q = useQuery({
    queryKey: ['portal-report', vid],
    queryFn: () => api.get<{ html: string; visit: { numero: number; restaurantNome: string }; canComment: boolean; canReply: boolean; replyable: Array<{ ncId: string; titulo: string; setor: string; reply: Reply | null }> }>(`/api/portal/visits/${vid}/report`),
  });
  useDocumentTitle(q.data ? `Relatório nº ${q.data.visit.numero} · ${q.data.visit.restaurantNome}` : 'Relatório');
  return (
    <div style={{ minHeight: '100vh' }}>
      <header className="topbar">
        <div className="topbar-in narrow">
          <Button size="xs" style={{ height: 36 }} onClick={() => (window.history.length > 1 ? nav(-1) : nav('/portal'))}>← Voltar</Button>
          <div className="flex1" />
          {q.data && <PdfButton endpoint={`/api/portal/visits/${vid}/pdf`} printUrl={`/r/visit/${vid}?print=1`} />}
        </div>
      </header>
      <main className="page narrow anim-fade">
        {css.data && <style>{css.data}</style>}
        {q.error && <ErrorState message={errMsg(q.error)} />}
        {!q.data && !q.error && <Loading rows={4} height={140} />}
        {q.data && (
          <div className="report-wrap">
            {q.data.canReply && q.data.replyable.length > 0 && (
              <section className="card lg" style={{ borderColor: 'var(--accent-border)', background: '#F8FBF9' }}>
                <h2 className="h2" style={{ marginBottom: 4 }}>Responda às pendências</h2>
                <div className="small" style={{ marginBottom: 6 }}>Seu plano de ação aparece para a nutricionista no acompanhamento e na próxima visita.</div>
                {q.data.replyable.map((n) => (
                  <div key={n.ncId} className="row gap-10" style={{ padding: '10px 0', borderTop: '1px solid var(--divider)' }}>
                    <div className="flex1">
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{n.titulo}</div>
                      <div className="small">{n.reply ? `Seu plano: ${n.reply.texto}` : n.setor}</div>
                    </div>
                    <Button size="xs" onClick={() => setReplying(n)}>{n.reply ? 'Editar' : 'Responder'}</Button>
                  </div>
                ))}
              </section>
            )}
            <ReportHtml html={q.data.html} />
            <Comments base={`/api/portal/visits/${vid}`} canComment={q.data.canComment} />
          </div>
        )}
      </main>
      <ReplySheet nc={replying} onClose={() => setReplying(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['portal-report', vid] }); qc.invalidateQueries({ queryKey: ['portal'] }); }} />
    </div>
  );
}
